import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { format, parseISO, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Lock, Sparkles, Check } from "lucide-react";
import { MaterialBadge } from "@/components/MaterialBadge";
import { CheckSeleccion } from "./BarraSeleccion";
import { ResumenOperativo, OjoOperativo } from "./ResumenOperativo";

const fmt = (d) => (d ? format(parseISO(d.slice(0, 10)), "EEE d MMM", { locale: es }) : "sin fecha");

/** Resumen operativo del conjunto: trabajos · destinos · en ruta · sin ruta (+ realizados/sugeridos si los hay). */
export function ResumenConjunto({ items, testId = "resumen-conjunto" }) {
  const r = useMemo(() => {
    const destinos = new Set(items.map((m) => m.espacio?.id || `${m.espacio?.nombre}|${m.espacio?.ciudad}`));
    const oper = items.filter((m) => !m.realizado && !m.sugerido);
    return { n: items.length, destinos: destinos.size, enRuta: oper.filter((m) => m.en_ruta).length, sinRuta: oper.filter((m) => !m.en_ruta).length,
      realizados: items.filter((m) => m.realizado).length, sugeridos: items.filter((m) => m.sugerido).length, sinGeo: items.filter((m) => m.espacio?.lat == null).length };
  }, [items]);
  return (
    <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3 gap-y-1" data-testid={testId}>
      <span><b className="text-zinc-100">{r.n}</b> trabajos</span><span>·</span><span><b className="text-zinc-100">{r.destinos}</b> destinos</span><span>·</span>
      <span><b className="text-amber-200">{r.enRuta}</b> en ruta</span><span>·</span><span><b className="text-emerald-200">{r.sinRuta}</b> sin ruta</span>
      {r.realizados > 0 && <span>· <b className="text-zinc-300">{r.realizados}</b> realizados</span>}
      {r.sugeridos > 0 && <span>· <b className="text-amber-300">{r.sugeridos}</b> sugeridos</span>}
      {r.sinGeo > 0 && <span className="text-rose-300" data-testid="resumen-sin-geo">· {r.sinGeo} sin geolocalizar</span>}
    </div>
  );
}

function Badges({ m }) {
  return (
    <>
      {m.realizado && <span className="text-[10px] px-1 rounded bg-emerald-500/20 text-emerald-200 inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> realizado</span>}
      {m.sugerido && <span className="text-[10px] px-1 rounded border border-dashed border-amber-400 text-amber-200 inline-flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> sugerido +{m.desvio_km} km</span>}
      {m.en_ruta && !m.realizado && <span className="text-[10px] px-1 rounded bg-amber-500/15 text-amber-200 inline-flex items-center gap-0.5"><Lock className="w-3 h-3" /> {m.en_ruta.codigo}</span>}
      {m.fecha_origen === "cita" && <span className="text-[10px] px-1 rounded bg-cyan-500/15 text-cyan-200">Cita / prevista</span>}
      <MaterialBadge disponibilidad={m.disponibilidad} recepcion={m.recepcion_resumen} componentes={m.componentes} compact testId={`material-badge-${m.id}`} />
    </>
  );
}

/** Vista Día: tarjetas cómodas de los montajes del día (según filtros). Sin DnD. */
export function DiaMontajes({ dia, setDia, items, provinciaColor, onVer, onCambiarFecha, onCardClick, seleccion }) {
  const key = format(dia, "yyyy-MM-dd");
  const delDia = useMemo(() => items.filter((m) => (m.fecha_visible || "").slice(0, 10) === key).sort((a, b) => Number(!!a.realizado) - Number(!!b.realizado) || (a.espacio?.provincia || "").localeCompare(b.espacio?.provincia || "", "es")), [items, key]);
  return (
    <div data-testid="cal-dia-view" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setDia(addDays(dia, -1))} data-testid="cal-dia-prev" className="bg-transparent border-zinc-700 h-8 px-2"><ChevronLeft className="w-4 h-4" /></Button>
        <input type="date" value={key} onChange={(e) => e.target.value && setDia(parseISO(e.target.value))} data-testid="cal-dia-input" className="h-8 text-sm bg-zinc-900/60 border border-zinc-800 text-zinc-200 rounded px-2" />
        <Button size="sm" variant="outline" onClick={() => setDia(addDays(dia, 1))} data-testid="cal-dia-next" className="bg-transparent border-zinc-700 h-8 px-2"><ChevronRight className="w-4 h-4" /></Button>
        <span className="text-sm font-semibold text-zinc-200 capitalize">{format(dia, "EEEE d 'de' MMMM yyyy", { locale: es })}</span>
        <div className="ml-auto"><ResumenConjunto items={delDia} testId="cal-dia-resumen" /></div>
      </div>
      {delDia.length === 0 && <div className="text-sm text-zinc-500 italic py-10 text-center" data-testid="cal-dia-vacio">Sin montajes este día con los filtros actuales.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
        {delDia.map((m) => (
          <div key={m.id} data-testid={`dia-card-${m.id}`} data-seleccionada={seleccion?.ids?.has(m.id) ? "1" : "0"} className={`rounded-lg border p-2.5 text-xs ${provinciaColor(m.espacio?.provincia)} ${m.realizado ? "opacity-50" : ""} ${m.sugerido ? "border-dashed" : ""} ${seleccion?.ids?.has(m.id) ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-zinc-950" : ""}`}>
            <div className="flex items-start gap-2">
              <CheckSeleccion m={m} seleccion={seleccion} size="w-4 h-4 mt-0.5" />
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onCardClick(m)}>
                <div className="font-semibold text-sm truncate">{m.espacio?.nombre || m.cliente_nombre}</div>
                <div className="font-mono text-[11px] opacity-80 truncate">{m.peticion?.codigo} · {m.peticion?.nombre}</div>
                <div className="text-[11px] opacity-70 truncate">{[m.espacio?.direccion, m.espacio?.ciudad, m.espacio?.provincia].filter(Boolean).join(" · ")}</div>
                {(m.referencia_cliente || m.descripcion) && <div className="text-[11px] opacity-70 italic truncate">{m.referencia_cliente || m.descripcion}</div>}
                <ResumenOperativo m={m} testId={`dia-resumen-operativo-${m.id}`} />
                <div className="flex flex-wrap items-center gap-1.5 mt-1"><Badges m={m} />{m.espacio?.horario && <span className="text-[10px] opacity-70">🕒 {m.espacio.horario}</span>}</div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <OjoOperativo m={m} onVer={onVer} size="w-4 h-4" className="p-1" testId={`dia-ver-${m.id}`} />
                {!m.sugerido && <button type="button" onClick={() => onCambiarFecha(m)} className={`p-1 rounded hover:bg-zinc-900/60 ${!m.reprogramable ? "opacity-40" : ""}`} title={m.reprogramable ? "Cambiar fecha" : m.bloqueo_motivo} data-testid={`dia-fecha-${m.id}`}><CalendarDays className="w-4 h-4" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FitBounds({ puntos }) {
  const map = useMap();
  useEffect(() => { if (puntos.length) map.fitBounds(puntos.map((p) => [p.lat, p.lon]), { padding: [30, 30], maxZoom: 12 }); }, [map, puntos]);
  return null;
}

const COLOR = { libre: "#34d399", en_ruta: "#fbbf24", realizado: "#71717a", sugerido: "#f59e0b" };
const tipoDe = (m) => (m.sugerido ? "sugerido" : m.realizado ? "realizado" : m.en_ruta ? "en_ruta" : "libre");

/** Vista Mapa: marcadores por destino (agrupa trabajos del mismo espacio). Sólo visualización. */
export function MapaMontajes({ items, onVer, seleccion }) {
  const grupos = useMemo(() => {
    const g = new Map();
    for (const m of items) {
      if (m.espacio?.lat == null || m.espacio?.lon == null) continue;
      const k = m.espacio.id || `${m.espacio.lat},${m.espacio.lon}`;
      const cur = g.get(k) || { key: k, lat: m.espacio.lat, lon: m.espacio.lon, espacio: m.espacio, items: [] };
      cur.items.push(m); g.set(k, cur);
    }
    return Array.from(g.values());
  }, [items]);
  const sinGeo = items.filter((m) => m.espacio?.lat == null).length;
  const colorGrupo = (gr) => { const t = gr.items.map(tipoDe); return t.includes("libre") ? COLOR.libre : t.includes("en_ruta") ? COLOR.en_ruta : t.includes("sugerido") ? COLOR.sugerido : COLOR.realizado; };
  return (
    <div data-testid="cal-mapa-view" className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-400">
        <span data-testid="mapa-destinos"><b className="text-zinc-100">{grupos.length}</b> destinos en mapa · <b className="text-zinc-100">{items.length - sinGeo}</b> trabajos</span>
        {sinGeo > 0 && <span className="text-rose-300" data-testid="mapa-sin-geo">{sinGeo} sin geolocalizar (excluidos)</span>}
        <span className="ml-auto flex gap-3">
          <span><i className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: COLOR.libre }} />pendiente libre</span>
          <span><i className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: COLOR.en_ruta }} />en ruta</span>
          <span><i className="inline-block w-2.5 h-2.5 rounded-full mr-1 border border-dashed border-amber-300" style={{ background: "transparent" }} />sugerido</span>
          <span><i className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: COLOR.realizado }} />realizado</span>
        </span>
      </div>
      <div className="rounded-xl overflow-hidden border border-zinc-800" style={{ height: "68vh" }}>
        <MapContainer center={[37.4, -5.9]} zoom={7} style={{ height: "100%", width: "100%" }} className="bg-zinc-900">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <FitBounds puntos={grupos} />
          {grupos.map((gr) => {
            const sug = gr.items.every((m) => m.sugerido);
            return (
              <CircleMarker key={gr.key} center={[gr.lat, gr.lon]} radius={8 + Math.min(gr.items.length, 6) * 1.5}
                pathOptions={{ color: colorGrupo(gr), fillColor: colorGrupo(gr), fillOpacity: sug ? 0.15 : 0.7, weight: 2, dashArray: sug ? "4 3" : undefined }}>
                <Popup>
                  <div className="text-xs min-w-[200px]" data-testid={`mapa-popup-${gr.key}`}>
                    <div className="font-bold">{gr.espacio.nombre}</div>
                    <div className="opacity-80">{[gr.espacio.ciudad, gr.espacio.provincia].filter(Boolean).join(" · ")}</div>
                    <div className="mt-1 font-semibold">{gr.items.length} trabajo(s)</div>
                    <ul className="mt-1 space-y-1 max-h-48 overflow-y-auto">
                      {gr.items.map((m) => (
                        <li key={m.id} className="border-t pt-1" data-testid={`mapa-item-${m.id}`}>
                          <div className="font-mono flex items-center gap-1"><CheckSeleccion m={m} seleccion={seleccion} size="w-3.5 h-3.5" />{m.peticion?.codigo} · {m.peticion?.nombre}</div>
                          <div>{fmt(m.fecha_visible)} · {m.sugerido ? `sugerido +${m.desvio_km} km` : m.realizado ? "realizado" : m.en_ruta ? `en ruta ${m.en_ruta.codigo}` : "sin ruta"}</div>
                          <div className="flex gap-2 mt-0.5"><button type="button" onClick={() => onVer(m)} className="underline">Ficha</button><a href={`/peticiones/${m.peticion?.id}`} className="underline">Ver petición</a></div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
